import { useEffect, useRef, useState } from "react";
import { MapPin, BookOpen, Code2 } from "lucide-react";
import useInView from "../lib/useInView";

const STATS = [
  { label: "Capstone Project", value: 1, suffix: "" },
  { label: "Certificates Earned", value: 2, suffix: "+" },
  { label: "Languages & Tools", value: 10, suffix: "+" },
];

function StatCounter({ label, value, suffix }) {
  const [count, setCount] = useState(0);
  const started = useRef(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting && !started.current) {
          started.current = true;
          const duration = 1600;
          const t0 = performance.now();
          const tick = (now) => {
            const prog = Math.min((now - t0) / duration, 1);
            const eased = 1 - Math.pow(1 - prog, 3);
            setCount(Math.round(eased * value));
            if (prog < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
          obs.unobserve(el);
        }
      },
      { threshold: 0.5 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [value]);

  return (
    <div ref={ref} className="text-center">
      <p className="font-mono text-4xl font-bold text-indigo-400 mb-1 tabular-nums">
        {count}
        {suffix}
      </p>
      <p className="text-[#94a3b8] text-sm">{label}</p>
    </div>
  );
}

export default function About() {
  const [sectionRef, visible] = useInView();

  return (
    <section id="about" className="section-padding bg-[#0a0a0a]">
      <div className="section-container" ref={sectionRef}>
        {/* Header */}
        <div
          className={`mb-14 transition-all duration-700 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <span className="section-label">// 01. about_me</span>
          <h2 className="section-heading">Who I Am</h2>
          <div className="section-divider" />
        </div>

        {/* Two-column */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
          {/* Photo */}
          <div
            className={`transition-all duration-700 delay-200 ${
              visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
            }`}
          >
            <div className="relative w-72 h-72 sm:w-80 sm:h-80 mx-auto lg:mx-0">
              <div className="absolute -inset-3 rounded-2xl border border-indigo-500/20 bg-gradient-to-br from-indigo-500/5 to-cyan-500/5" />
              <div className="absolute -inset-1.5 rounded-xl border border-indigo-500/10" />
              <div className="relative w-full h-full rounded-xl overflow-hidden border border-[#1e293b] bg-[#111111] flex items-center justify-center">
                <img
                  src="/assets/profile.png"
                  alt="Khadim Hussain"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
                {/* <span className="absolute font-mono text-6xl font-bold text-indigo-500/25 select-none">
                  KH
                </span> */}
              </div>
              <div className="absolute -bottom-4 -right-4 w-20 h-20 border-b-2 border-r-2 border-indigo-500/40 rounded-br-xl" />
              <div className="absolute -top-4 -left-4 w-20 h-20 border-t-2 border-l-2 border-cyan-500/40 rounded-tl-xl" />
            </div>
          </div>

          {/* Text */}
          <div
            className={`transition-all duration-700 delay-300 ${
              visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"
            }`}
          >
            <div className="space-y-4 text-[#94a3b8] leading-relaxed mb-8">
              <p>
                I recently graduated with a{" "}
                <span className="text-[#f1f5f9] font-medium">
                  BS in Computer Science
                </span>{" "}
                and spent my final year building AlgoViz — an interactive
                algorithm visualizer in React that helped me go deep on
                JavaScript, component state management, and how to make abstract
                technical concepts tangible.
              </p>
              <p>
                I'm strongest in{" "}
                <span className="font-mono text-sm text-indigo-400">
                  Python
                </span>
                ,{" "}
                <span className="font-mono text-sm text-indigo-400">
                  JavaScript
                </span>
                , and{" "}
                <span className="font-mono text-sm text-indigo-400">React</span>
                . Although I'm comfortable working across the stack, I'm
                particularly drawn to backend development. I'm currently
                expanding my knowledge of REST APIs, Node.js, and databases to
                build robust, scalable applications.
              </p>
              <p>
                I'm looking for a{" "}
                <span className="text-[#f1f5f9] font-medium">
                  junior or entry-level role
                </span>{" "}
                where I can contribute, keep learning, and work alongside
                engineers with production experience.
              </p>
            </div>

            <div className="flex flex-wrap gap-4 text-sm text-[#94a3b8] mb-8">
              <span className="flex items-center gap-1.5">
                <MapPin size={13} className="text-indigo-400" />
                Karachi, Pakistan
              </span>
              <span className="flex items-center gap-1.5">
                <BookOpen size={13} className="text-cyan-400" />
                CS Graduate 2026
              </span>
              <span className="flex items-center gap-1.5">
                <Code2 size={13} className="text-green-400" />
                Building in public
              </span>
            </div>

            {/* Terminal snippet */}
            <div className="rounded-xl border border-[#1e293b] bg-[#111111] overflow-hidden">
              <div className="flex items-center gap-1.5 px-4 py-2 border-b border-[#1e293b] bg-[#0d0d0d]">
                <span className="w-3 h-3 rounded-full bg-red-500/70" />
                <span className="w-3 h-3 rounded-full bg-yellow-500/70" />
                <span className="w-3 h-3 rounded-full bg-green-500/70" />
                <span className="ml-2 font-mono text-xs text-[#94a3b8]">
                  terminal
                </span>
              </div>
              <div className="p-4 font-mono text-sm space-y-2">
                <p>
                  <span className="text-indigo-400">$ </span>
                  <span className="text-[#94a3b8]">cat status.txt</span>
                </p>
                <p className="text-green-400 pl-2">
                  Fresh grad. Actively job hunting. Open to learning.
                </p>
                <p>
                  <span className="text-indigo-400">$ </span>
                  <span className="text-[#94a3b8]">
                    cat currently_learning.txt
                  </span>
                </p>
                <p className="text-cyan-400 pl-2">REST APIs, Node.js.</p>
                <p className="flex items-center gap-0.5">
                  <span className="text-indigo-400">$ </span>
                  <span className="animate-[blink_1s_step-end_infinite] text-[#f1f5f9] ml-0.5">
                    _
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Stat counters */}
        <div
          className={`grid grid-cols-3 gap-8 p-8 rounded-2xl border border-[#1e293b] bg-[#111111] transition-all duration-700 delay-500 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          {STATS.map((s) => (
            <StatCounter key={s.label} {...s} />
          ))}
        </div>
      </div>
    </section>
  );
}
